/**
 * Mobile Search Controls - Free Agent Search
 * Compact search/filter UI for mobile
 */

import React, { useState, useEffect } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { validateCustomWindow } from '../../../../utils/customWindowValidator';

const C = dynastyTheme.tokens.colors;

const MobileSearchControls = ({ state, leagueId, isCommissionerMode, activeTeamName }) => {
  const {
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    positionFilter,
    setPositionFilter,
    showAll,
    setShowAll,
    advancedFilters,
    setAdvancedFilters,
    includeNon40Man,
    setIncludeNon40Man,
    showRecent,
    setShowRecent,
  } = state;

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // 9m Root Cause #2: key alignment to useSearchFilters / usePlayerData contract.
  //   minGames/maxGames → minG/maxG          (hook reads advancedFilters.minG)
  //   mlbQualifiedHitters → hitterQualified  (hook reads advancedFilters.hitterQualified)
  //   mlbQualifiedPitchers → pitcherQualified (hook reads advancedFilters.pitcherQualified)
  // Pre-9m these mobile keys never matched the hook's reader → filters silently skipped even
  // after the missing pass-through was fixed.
  const [localFilters, setLocalFilters] = useState(advancedFilters || {
    dateRange: 'season',
    customStartDate: '',
    customEndDate: '',
    minAB: '',
    maxAB: '',
    minHR: '',
    maxHR: '',
    minG: '',
    maxG: '',
    hitterQualified: false,
    minK: '',
    maxK: '',
    minGS: '',
    maxGS: '',
    minIP: '',
    maxIP: '',
    pitcherQualified: false,
    minPrice: '',
    maxPrice: ''
  });

  // 9o.6.e Commit 8: MLB qualification thresholds apply to season stats only.
  // Greyed out + force-false when the user has picked a rolling window.
  const qualificationDisabled = (localFilters?.dateRange ?? 'season') !== 'season';
  const qualificationDisabledTooltip =
    'Qualification thresholds (PA ≥ 502, IP ≥ 162) apply to season stats only. ' +
    'Switch to Season window to enable.';

  // Clear pending qualification flags when window switches off season, so the
  // user's apply step doesn't push a stale `true` upstream.
  useEffect(() => {
    if (qualificationDisabled && (localFilters?.hitterQualified || localFilters?.pitcherQualified)) {
      setLocalFilters(prev => ({ ...prev, hitterQualified: false, pitcherQualified: false }));
    }
  }, [qualificationDisabled, localFilters?.hitterQualified, localFilters?.pitcherQualified]);

  const hasActiveFilters = () => {
    if (!advancedFilters) return false;
    return Object.entries(advancedFilters).some(([key, value]) => {
      if (key === 'dateRange') return value !== 'season';
      if (typeof value === 'boolean') return value === true;
      return value !== '' && value !== null && value !== undefined;
    });
  };

  // 9o.6.e Slice 2 Commit 8-rest: compute custom-window validation for inline
  // feedback + Apply-button gate. Skip when not in custom mode (validation.valid=true
  // for presets so the Apply button isn't blocked).
  const customValidation =
    localFilters.dateRange === 'custom'
      ? validateCustomWindow(localFilters.customStartDate, localFilters.customEndDate)
      : { valid: true, errors: {}, throwMessage: null };
  const customBothEmpty =
    localFilters.dateRange === 'custom' &&
    !localFilters.customStartDate &&
    !localFilters.customEndDate;
  const showCustomStartError =
    localFilters.dateRange === 'custom' && !customBothEmpty && customValidation.errors.start;
  const showCustomEndError =
    localFilters.dateRange === 'custom' && !customBothEmpty && customValidation.errors.end;
  // Per NO FALLBACKS: don't let user submit known-invalid state. Initial empty
  // state is "not yet valid but not erroneous" — Apply stays disabled until both
  // dates entered AND pass validation.
  const applyDisabled =
    localFilters.dateRange === 'custom' && !customValidation.valid;

  const handleApplyFilters = () => {
    if (applyDisabled) return;
    setAdvancedFilters(localFilters);
    setShowAdvancedFilters(false);
  };

  const handleClearFilters = () => {
    // 9m: keys aligned to hook contract (see localFilters comment above).
    const clearedFilters = {
      dateRange: 'season',
      customStartDate: '',
      customEndDate: '',
      minAB: '',
      maxAB: '',
      minHR: '',
      maxHR: '',
      minG: '',
      maxG: '',
      hitterQualified: false,
      minK: '',
      maxK: '',
      minGS: '',
      maxGS: '',
      minIP: '',
      maxIP: '',
      pitcherQualified: false,
      minPrice: '',
      maxPrice: ''
    };
    setLocalFilters(clearedFilters);
    setAdvancedFilters(clearedFilters);
  };

  // Position options
  const hitterPositions = [
    { value: 'all', label: 'All' },
    { value: 'C', label: 'C' },
    { value: '1B', label: '1B' },
    { value: '2B', label: '2B' },
    { value: '3B', label: '3B' },
    { value: 'SS', label: 'SS' },
    { value: 'OF', label: 'OF' },
    { value: 'CI', label: 'CI' },
    { value: 'MI', label: 'MI' },
    { value: 'DH', label: 'DH' }
  ];

  const pitcherPositions = [
    { value: 'all', label: 'All' },
    { value: 'SP', label: 'SP' },
    { value: 'RP', label: 'RP' }
  ];

  const positions = activeTab === 'hitting' ? hitterPositions : pitcherPositions;

  return (
    // 8px matches the canonical tile-rhythm gap on MobileTeamHome (e.g.,
    // Box Score tile marginTop, line 509). Applied symmetrically:
    //   - marginTop:8 between MobilePageHeader and this tile
    //   - marginBottom:8 between this tile and MobilePlayerTable below
    // The filters row's own marginBottom was previously contributing extra
    // dead space and is now 0 — the outer marginBottom carries the gap.
    <div style={{ marginTop: '8px', marginBottom: '8px' }}>
      {/* Tab Navigation - REMOVED (moved inside table box) */}

      {/* Search Input — 9m sizing reduced to match advanced-panel inputs (Tony: "absurdly large") */}
      <div style={{
        position: 'relative',
        marginBottom: '8px'
      }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: dynastyTheme.tokens.colors.neutral[500]
          }}
        />
        <input
          type="text"
          placeholder="Search player name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 10px 6px 30px',
            backgroundColor: dynastyTheme.tokens.colors.neutral[800],
            color: C.white,
            border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderRadius: '6px',
            fontSize: '11px',
            outline: 'none'
          }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: dynastyTheme.tokens.colors.neutral[500]
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filters Row — three equal-width controls (flex: 1 each) with 8px
          gaps. Edge-to-edge layout fills the row without dead space. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '0'
      }}>
        {/* Position Filter — flex: 1 for equal-width with the two buttons. */}
        <div style={{ flex: 1, height: '32px' }}>
          <select
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            style={{
              width: '100%',
              height: '32px',
              boxSizing: 'border-box',
              padding: '0 10px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[800],
              color: C.white,
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {positions.map(pos => (
              <option key={pos.value} value={pos.value}>
                Pos: {pos.label}
              </option>
            ))}
          </select>
        </div>

        {/* Advanced Filters Toggle — flex: 1 + justifyContent center. */}
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          style={{
            flex: 1,
            height: '32px',
            boxSizing: 'border-box',
            padding: '0 10px',
            backgroundColor: showAdvancedFilters
              ? dynastyTheme.tokens.colors.neutral[700]
              : dynastyTheme.tokens.colors.neutral[800],
            color: showAdvancedFilters ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[400],
            border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
        >
          <SlidersHorizontal size={14} />
          Filters
        </button>

        {/* Recent — flex: 1 + justifyContent center. */}
        <button
          onClick={() => setShowRecent(!showRecent)}
          aria-pressed={showRecent}
          style={{
            flex: 1,
            height: '32px',
            boxSizing: 'border-box',
            padding: '0 10px',
            backgroundColor: showRecent
              ? dynastyTheme.tokens.colors.neutral[700]
              : dynastyTheme.tokens.colors.neutral[800],
            color: showRecent ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[400],
            border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
        >
          {showRecent ? '✓' : '☐'} Recent
        </button>
      </div>

      {/* Commissioner: Show Full Universe toggle */}
      {isCommissionerMode && setIncludeNon40Man && (
        <div style={{ marginBottom: '8px' }}>
          <button
            onClick={() => setIncludeNon40Man(!includeNon40Man)}
            style={{
              width: '100%', padding: '6px 10px',
              backgroundColor: includeNon40Man ? dynastyTheme.tokens.colors.primary + '20' : dynastyTheme.tokens.colors.neutral[800],
              color: includeNon40Man ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
              border: `1px solid ${includeNon40Man ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            {includeNon40Man ? '✓ Showing All Players (Commissioner)' : 'Show Full Universe (Commissioner)'}
          </button>
        </div>
      )}

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div style={{
          backgroundColor: dynastyTheme.tokens.colors.neutral[850],
          border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderRadius: '8px',
          padding: '12px',
          marginTop: '12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <h4 style={{
              fontSize: '13px',
              fontWeight: '600',
              color: dynastyTheme.tokens.colors.primary,
              margin: 0
            }}>
              Advanced Filters
            </h4>
            <button
              onClick={() => setShowAdvancedFilters(false)}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                color: dynastyTheme.tokens.colors.neutral[500]
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Date Range */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '600',
              color: dynastyTheme.tokens.colors.neutral[400],
              marginBottom: '4px'
            }}>
              Date Range
            </label>
            <select
              value={localFilters.dateRange}
              onChange={(e) => setLocalFilters({ ...localFilters, dateRange: e.target.value })}
              style={{
                width: '100%',
                padding: '6px 8px',
                backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                color: C.white,
                border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                borderRadius: '4px',
                fontSize: '11px',
                outline: 'none'
              }}
            >
              <option value="season">Full Season</option>
              <option value="last7">Last 7 Days</option>
              <option value="last14">Last 14 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {localFilters.dateRange === 'custom' && (
            <div style={{ marginBottom: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={localFilters.customStartDate}
                  onChange={(e) => setLocalFilters({ ...localFilters, customStartDate: e.target.value })}
                  aria-invalid={!!showCustomStartError}
                  aria-label="Custom window start date"
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                    color: C.white,
                    border: `1px solid ${showCustomStartError ? dynastyTheme.tokens.colors.error : dynastyTheme.tokens.colors.neutral[700]}`,
                    borderRadius: '4px',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                />
                {showCustomStartError && (
                  <span
                    role="alert"
                    data-testid="fa-custom-start-error-mobile"
                    style={{
                      display: 'block',
                      fontSize: '10px',
                      color: dynastyTheme.tokens.colors.error,
                      marginTop: '4px',
                    }}
                  >
                    {customValidation.errors.start}
                  </span>
                )}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={localFilters.customEndDate}
                  onChange={(e) => setLocalFilters({ ...localFilters, customEndDate: e.target.value })}
                  aria-invalid={!!showCustomEndError}
                  aria-label="Custom window end date"
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                    color: C.white,
                    border: `1px solid ${showCustomEndError ? dynastyTheme.tokens.colors.error : dynastyTheme.tokens.colors.neutral[700]}`,
                    borderRadius: '4px',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                />
                {showCustomEndError && (
                  <span
                    role="alert"
                    data-testid="fa-custom-end-error-mobile"
                    style={{
                      display: 'block',
                      fontSize: '10px',
                      color: dynastyTheme.tokens.colors.error,
                      marginTop: '4px',
                    }}
                  >
                    {customValidation.errors.end}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Hitter-specific filters */}
          {activeTab === 'hitting' && (
            <>
              {/* At Bats */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  At Bats (AB)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minAB}
                    onChange={(e) => setLocalFilters({ ...localFilters, minAB: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxAB}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxAB: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Home Runs */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Home Runs (HR)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minHR}
                    onChange={(e) => setLocalFilters({ ...localFilters, minHR: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxHR}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxHR: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Games */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Games (G)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minG}
                    onChange={(e) => setLocalFilters({ ...localFilters, minG: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxG}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxG: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* MLB Qualified Hitters */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={qualificationDisabled ? false : localFilters.hitterQualified}
                    disabled={qualificationDisabled}
                    onChange={(e) => setLocalFilters({ ...localFilters, hitterQualified: e.target.checked })}
                    style={{
                      width: '16px', height: '16px',
                      cursor: qualificationDisabled ? 'not-allowed' : 'pointer',
                      opacity: qualificationDisabled ? 0.5 : 1,
                    }}
                    title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  />
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: dynastyTheme.tokens.colors.neutral[400],
                    opacity: qualificationDisabled ? 0.5 : 1,
                  }}
                  title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  >
                    MLB Qualified Only (≥502 PA)
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Pitcher-specific filters */}
          {activeTab === 'pitching' && (
            <>
              {/* Strikeouts */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Strikeouts (K)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minK}
                    onChange={(e) => setLocalFilters({ ...localFilters, minK: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxK}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxK: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Games Started */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Games Started (GS)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minGS}
                    onChange={(e) => setLocalFilters({ ...localFilters, minGS: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxGS}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxGS: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Innings Pitched */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  marginBottom: '4px'
                }}>
                  Innings Pitched (IP)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minIP}
                    onChange={(e) => setLocalFilters({ ...localFilters, minIP: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxIP}
                    onChange={(e) => setLocalFilters({ ...localFilters, maxIP: e.target.value })}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                      color: C.white,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* MLB Qualified Pitchers */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={qualificationDisabled ? false : localFilters.pitcherQualified}
                    disabled={qualificationDisabled}
                    onChange={(e) => setLocalFilters({ ...localFilters, pitcherQualified: e.target.checked })}
                    style={{
                      width: '16px', height: '16px',
                      cursor: qualificationDisabled ? 'not-allowed' : 'pointer',
                      opacity: qualificationDisabled ? 0.5 : 1,
                    }}
                    title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  />
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: dynastyTheme.tokens.colors.neutral[400],
                    opacity: qualificationDisabled ? 0.5 : 1,
                  }}
                  title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  >
                    MLB Qualified Only (≥162 IP)
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Price Range (both tabs) */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '600',
              color: dynastyTheme.tokens.colors.neutral[400],
              marginBottom: '4px'
            }}>
              Price Range ($)
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <input
                type="number"
                placeholder="Min"
                step="0.5"
                value={localFilters.minPrice}
                onChange={(e) => setLocalFilters({ ...localFilters, minPrice: e.target.value })}
                style={{
                  padding: '6px 8px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                  color: C.white,
                  border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                  borderRadius: '4px',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
              <input
                type="number"
                placeholder="Max"
                step="0.5"
                value={localFilters.maxPrice}
                onChange={(e) => setLocalFilters({ ...localFilters, maxPrice: e.target.value })}
                style={{
                  padding: '6px 8px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                  color: C.white,
                  border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                  borderRadius: '4px',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '16px' }}>
            <button
              onClick={handleClearFilters}
              style={{
                padding: '8px 12px',
                backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                color: dynastyTheme.tokens.colors.neutral[400],
                border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilters}
              disabled={applyDisabled}
              aria-disabled={applyDisabled}
              data-testid="fa-mobile-apply-filters"
              style={{
                padding: '8px 12px',
                backgroundColor: applyDisabled
                  ? dynastyTheme.tokens.colors.neutral[700]
                  : dynastyTheme.tokens.colors.primary,
                color: applyDisabled ? dynastyTheme.tokens.colors.neutral[400] : C.black,
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: applyDisabled ? 'not-allowed' : 'pointer',
                opacity: applyDisabled ? 0.6 : 1,
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSearchControls;
